
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { getZapierWebhook, saveZapierWebhook } from "@/services/templateService";
import { useToast } from "@/components/ui/use-toast";
import { Save, ExternalLink, Loader2 } from "lucide-react";

export const ZapierSettings = () => {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Load saved webhook URL
    setWebhookUrl(getZapierWebhook());
  }, []);

  const handleSave = () => {
    setIsSaving(true);
    try {
      saveZapierWebhook(webhookUrl);
      toast({
        title: "Success",
        description: "Zapier webhook URL saved successfully",
      });
    } catch (error) {
      console.error('Error saving webhook URL:', error);
      toast({
        title: "Error",
        description: "Failed to save webhook URL",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (!webhookUrl) {
      toast({
        title: "Error",
        description: "Please enter a webhook URL first",
        variant: "destructive",
      });
      return;
    }

    setIsTesting(true);
    try {
      const response = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors", // Handle CORS issues
        body: JSON.stringify({
          test: true,
          timestamp: new Date().toISOString(),
          message: "This is a test message from wassi.chat template manager"
        }),
      });

      toast({
        title: "Test Sent",
        description: "Test message sent to Zapier webhook. Check your Zap history to confirm it was received.",
      });
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast({
        title: "Note",
        description: "Request sent, but could not verify if it was received. Check your Zap history.",
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Zapier Integration</CardTitle>
        <CardDescription>
          Configure your Zapier webhook to automatically send your wassi.chat templates to other applications.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="webhook-url">Zapier Webhook URL</Label>
          <Input 
            id="webhook-url" 
            value={webhookUrl} 
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://hooks.zapier.com/hooks/catch/..." 
          />
          <p className="text-xs text-gray-400">
            Enter the webhook URL from your Zapier account. This is used to trigger automations when templates are applied.
          </p>
        </div>
        
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Button onClick={handleSave} disabled={isSaving} className="flex-1">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Webhook URL
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleTest} disabled={isTesting || !webhookUrl} className="flex-1">
              {isTesting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Test Webhook
                </>
              )}
            </Button>
          </div>
          
          <div className="bg-gray-900/50 rounded-md p-4">
            <h3 className="text-sm font-medium mb-2">How to set up your Zapier webhook:</h3>
            <ol className="text-sm space-y-2 text-gray-300 list-decimal pl-5">
              <li>In Zapier, create a new Zap with a "Webhook" trigger</li>
              <li>Select "Catch Hook" as the webhook type</li>
              <li>Copy the webhook URL provided by Zapier</li>
              <li>Paste it here and click "Save Webhook URL"</li>
              <li>Use the "Test Webhook" button to test the connection</li>
              <li>Complete your Zap setup in Zapier by adding actions</li>
            </ol>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
